import { useCallback, useEffect, useId, useMemo, useRef, useState, type ReactNode } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { Check, X } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils/cn';

/**
 * T-0114 / E10 — Shared "config editor drawer" shell.
 *
 * Visual contract: `design_handoff_caliber_v2/prototype/modals-round4b.js`
 * (`renderConfigDrawer`). The four config-CRUD editors in this ticket
 * (rubric, domains, checkpoints, widget-defaults) share the same drawer
 * pattern — breadcrumb / title / sub above a scrollable body, footer with
 * Cancel / Save draft / Save & apply — so we build the shell once here
 * and let each editor pass its own summary tiles, body sections, and
 * save handler.
 *
 * Why a custom shell on top of Radix Dialog:
 *   - The drawer is right-anchored at the prototype's `.cfgdrawer` size
 *     (≈920px wide, full-height), with a fixed footer rail that doesn't
 *     scroll with the body. The citation-source modal's centered card
 *     pattern doesn't fit; a fresh shell is cheaper than parameterizing
 *     that one for two opposing layouts.
 *   - We still build on Radix's Root / Portal / Overlay / Content
 *     primitives so we get focus trap, Esc handling, scrim semantics,
 *     and aria-modal labeling for free.
 *
 * Dirty-state guard (T-0114 §1):
 *   - Callers pass `isDirty`; when true, an attempted close prompts the
 *     consultant with a confirm() dialog ("Discard unsaved changes?"). The
 *     ESC keypress is intercepted via Radix's `onOpenChange` and the
 *     scrim's `onPointerDown` so we cover both close vectors. If the user
 *     confirms, we call `onOpenChange(false)` ourselves; if they cancel,
 *     we keep the modal open.
 *   - `confirm()` is a synchronous browser modal — acceptable for an
 *     unsaved-changes prompt, no need to ship a custom confirm dialog in
 *     this ticket. A future styling pass can swap this for a Radix
 *     AlertDialog without changing callers.
 *
 * Save flow:
 *   - The `Save & apply` button calls `onSave({ closeOnSuccess: true })`.
 *   - The `Save draft` button calls `onSave({ closeOnSuccess: false })`.
 *   - Each editor wires its own mutation in; the shell exposes
 *     `saving` so the buttons can spin. `saveError` renders a small inline
 *     band above the footer when the mutation rejected.
 *   - When `onSave` is omitted (read-only editors like parse-tree are
 *     served via a different shell), the footer collapses to a single
 *     Close button.
 */

export interface ConfigEditorModalProps {
  /** Whether the drawer is open. Wired to the global host's state. */
  open: boolean;
  /**
   * Open/close request from the modal (X button, ESC, scrim, save). The
   * shell intercepts close requests when `isDirty` is true.
   */
  onOpenChange: (open: boolean) => void;
  /** Small uppercase breadcrumb shown above the title. */
  bread?: ReactNode;
  /** Modal title (rendered as Dialog.Title). */
  title: ReactNode;
  /** Sub-heading under the title. */
  sub?: ReactNode;
  /** Optional right-side header content (status chip, version label, etc.). */
  headerRight?: ReactNode;
  /** Optional banner below the title — used for the "drift from default" warning. */
  warning?: ReactNode;
  /** Optional 4-tile KPI summary strip rendered at the top of the body. */
  summary?: ReactNode;
  /** Scrollable body content (sections + AI-proposal cards). */
  children: ReactNode;
  /** Footer note text rendered on the left. */
  foot?: ReactNode;
  /** Caller's dirty-state flag — when true, close requests confirm-prompt. */
  isDirty?: boolean;
  /** Optional save handler. When omitted the footer collapses to a Close-only state. */
  onSave?: (opts: { closeOnSuccess: boolean }) => Promise<void> | void;
  /** Set true while a save mutation is in flight; disables the save buttons. */
  saving?: boolean;
  /** Save-error band rendered above the footer when the last save rejected. */
  saveError?: ReactNode;
  /** Hides the "Save draft" affordance — single-action editors set this. */
  hideSaveDraft?: boolean;
  /** Override the "Save & apply" button label. */
  saveLabel?: string;
  /** Hide the entire footer (read-only modals use this). */
  hideFooter?: boolean;
}

export function ConfigEditorModal({
  open,
  onOpenChange,
  bread,
  title,
  sub,
  headerRight,
  warning,
  summary,
  children,
  foot,
  isDirty,
  onSave,
  saving,
  saveError,
  hideSaveDraft,
  saveLabel = 'Save & apply',
  hideFooter,
}: ConfigEditorModalProps) {
  const titleId = useId();

  const requestClose = useCallback(() => {
    if (isDirty) {
      const ok = window.confirm('You have unsaved changes. Discard them and close the editor?');
      if (!ok) return;
    }
    onOpenChange(false);
  }, [isDirty, onOpenChange]);

  const handleOpenChange = useCallback(
    (next: boolean) => {
      if (next) {
        onOpenChange(true);
        return;
      }
      requestClose();
    },
    [onOpenChange, requestClose],
  );

  return (
    <Dialog.Root open={open} onOpenChange={handleOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay
          className={cn(
            'fixed inset-0 z-40 bg-scrim',
            'opacity-0 motion-safe:transition-opacity motion-safe:duration-150',
            'data-[state=open]:opacity-100',
          )}
        />
        <Dialog.Content
          aria-labelledby={titleId}
          className={cn(
            // Right-anchored drawer, full height. The prototype renders
            // ~920px wide at desktop; clamp to the viewport on smaller
            // screens.
            'fixed inset-y-0 right-0 z-50 grid w-[min(960px,100vw)]',
            'grid-rows-[auto,1fr,auto] overflow-hidden bg-paper-0 shadow-modal',
            'translate-x-full motion-safe:transition-transform motion-safe:duration-200',
            'data-[state=open]:translate-x-0',
          )}
          onEscapeKeyDown={(ev) => {
            if (isDirty) {
              ev.preventDefault();
              requestClose();
            }
          }}
          onPointerDownOutside={(ev) => {
            if (isDirty) {
              ev.preventDefault();
              requestClose();
            }
          }}
        >
          <div className="flex items-start gap-3 border-b border-ink-100 px-6 py-4">
            <div className="min-w-0 flex-1">
              {bread ? (
                <div className="text-meta uppercase tracking-wide text-ink-500">{bread}</div>
              ) : null}
              <Dialog.Title
                id={titleId}
                className="mt-1 truncate text-h2 font-semibold text-ink-900"
              >
                {title}
              </Dialog.Title>
              {sub ? <p className="mt-1 text-meta text-ink-500">{sub}</p> : null}
            </div>
            <div className="flex flex-none items-center gap-2">
              {headerRight}
              <Dialog.Close
                aria-label="Close editor"
                onClick={(ev) => {
                  if (isDirty) {
                    ev.preventDefault();
                    requestClose();
                  }
                }}
                className={cn(
                  'inline-flex h-8 w-8 items-center justify-center rounded-card text-ink-500',
                  'hover:bg-paper-100 hover:text-ink-900',
                  'focus:outline-none focus-visible:ring-2 focus-visible:ring-ink-300',
                )}
              >
                <X className="h-4 w-4" aria-hidden="true" />
              </Dialog.Close>
            </div>
          </div>

          <div className="overflow-y-auto bg-paper-50">
            {summary ? (
              <div className="grid grid-cols-2 gap-3 border-b border-ink-100 bg-paper-0 px-6 py-4 sm:grid-cols-4">
                {summary}
              </div>
            ) : null}
            {warning ? (
              <div
                role="status"
                className="mx-6 mt-4 flex items-start gap-2 rounded-card border border-posture-at-risk-tint bg-posture-at-risk-tint px-3 py-2 text-body-sm text-posture-at-risk"
              >
                {warning}
              </div>
            ) : null}
            <div className="space-y-4 px-6 py-4">{children}</div>
          </div>

          {hideFooter ? null : (
            <div className="border-t border-ink-100 bg-paper-0">
              {saveError ? (
                <div className="px-6 pt-3" role="alert">
                  {saveError}
                </div>
              ) : null}
              <div className="flex items-center justify-between gap-3 px-6 py-3">
                <div className="text-meta text-ink-500">
                  {foot ?? 'Changes write through methodology override · audit-logged'}
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={requestClose}
                    disabled={saving}
                  >
                    Cancel
                  </Button>
                  {onSave && !hideSaveDraft ? (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={saving || !isDirty}
                      onClick={() => {
                        void onSave({ closeOnSuccess: false });
                      }}
                    >
                      Save draft
                    </Button>
                  ) : null}
                  {onSave ? (
                    <Button
                      type="button"
                      size="sm"
                      className="bg-accent-amber-600 text-paper-0 hover:bg-accent-amber-700"
                      disabled={saving || !isDirty}
                      onClick={() => {
                        void onSave({ closeOnSuccess: true });
                      }}
                    >
                      <Check className="h-3.5 w-3.5" aria-hidden="true" />
                      {saveLabel}
                    </Button>
                  ) : null}
                </div>
              </div>
            </div>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

/**
 * KPI summary tile used inside `summary`. Matches the prototype's
 * `.cfg-summary > div` shape: label / value / sub. The value can carry
 * an "ok" / "warn" / "crit" tone via the `tone` prop.
 */
export function ConfigSummaryTile({
  label,
  value,
  sub,
  tone,
}: {
  label: ReactNode;
  value: ReactNode;
  sub?: ReactNode;
  tone?: 'default' | 'ok' | 'warn' | 'crit';
}) {
  const toneClass =
    tone === 'ok'
      ? 'text-posture-healthy'
      : tone === 'warn'
        ? 'text-posture-at-risk'
        : tone === 'crit'
          ? 'text-posture-critical'
          : 'text-ink-900';
  return (
    <div className="space-y-0.5">
      <div className="text-meta text-ink-500">{label}</div>
      <div className={cn('text-h3 font-semibold tabular-nums', toneClass)}>{value}</div>
      {sub ? <div className="text-meta text-ink-500">{sub}</div> : null}
    </div>
  );
}

/**
 * Section card with a sticky-ish heading row. Editor modals stack 2–4
 * of these inside the body — "Active checkpoints", "AI proposals", etc.
 * AI sections set `tone="ai"` to flip the surface tint to the lavender
 * AI palette without inlining the colors.
 */
export function ConfigSection({
  heading,
  meta,
  actions,
  children,
  tone = 'default',
}: {
  heading: ReactNode;
  meta?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  tone?: 'default' | 'ai';
}) {
  const isAi = tone === 'ai';
  return (
    <section
      className={cn(
        'overflow-hidden rounded-card border',
        isAi ? 'border-ai-surface-border bg-ai-surface/40' : 'border-ink-100 bg-paper-0',
      )}
    >
      <header
        className={cn(
          'flex items-center justify-between gap-3 border-b px-4 py-2.5',
          isAi ? 'border-ai-surface-border' : 'border-ink-100',
        )}
      >
        <div className="min-w-0">
          <div className={cn('text-body-sm font-semibold', isAi ? 'text-ai-ink' : 'text-ink-900')}>
            {heading}
          </div>
          {meta ? <div className="text-meta text-ink-500">{meta}</div> : null}
        </div>
        {actions ? <div className="flex flex-none items-center gap-2">{actions}</div> : null}
      </header>
      <div className={cn('divide-y', isAi ? 'divide-ai-surface-border' : 'divide-ink-100')}>
        {children}
      </div>
    </section>
  );
}

/**
 * Hook that returns a stable "dirty" flag from a value-comparison against
 * a baseline snapshot. Editors call it once per form-state slice:
 *
 *   const isDirty = useIsDirty(initial, current);
 *
 * Comparison is structural (JSON.stringify). For these small config-CRUD
 * forms it's plenty cheap; if a future editor needs deeper diffing we
 * can swap in a real deep-equal helper.
 */
export function useIsDirty<T>(initial: T, current: T): boolean {
  return useMemo(() => {
    return JSON.stringify(initial) !== JSON.stringify(current);
  }, [initial, current]);
}

/**
 * Hook that keeps a "draft" snapshot of an upstream value in sync.
 * Returns the draft + a setter; calling `reset()` rolls back to the
 * latest upstream value (used when the user cancels mid-edit and
 * re-opens). The upstream value is the source of truth; the draft is
 * a working copy.
 */
export function useDraftState<T>(initial: T): {
  draft: T;
  setDraft: (next: T | ((prev: T) => T)) => void;
  reset: () => void;
  baseline: T;
} {
  const [draft, setDraft] = useState<T>(initial);
  const baselineRef = useRef<T>(initial);
  // Re-seed when the upstream changes (e.g. after a successful save the
  // editor refetches and the modal re-mounts under the new baseline).
  useEffect(() => {
    baselineRef.current = initial;
    setDraft(initial);
  }, [initial]);
  const reset = useCallback(() => setDraft(baselineRef.current), []);
  return { draft, setDraft, reset, baseline: baselineRef.current };
}
